/**
 * Quality tab content for the Collection Detail page.
 *
 * Per `feature-spec.md` §2.6 — two cards:
 *   1. Inter-Annotator Agreement — overall IAA + per-dimension list
 *      (each row with a coloured dot from the dimension palette and an
 *      IAA value coloured by tone).
 *   2. Adjudication Overview — adjudication rate, disputed count,
 *      agreed count.
 *
 * Per-dimension only — per-channel IAA is exposed by the API for future
 * use but is not rendered here (work-specs §A11).
 */

import React from 'react';

import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { cn } from '../../../../lib/utils';
import { getDimensionColor } from '../shared/dimensions';
import { getIaaTone, IAA_TONE_TEXT_CLASS } from '../shared/iaa';
import { useCollectionQuality } from './hooks/useDatasets';
import type { Collection } from './types';
import EmptyTabState from './components/EmptyTabState';

interface Props {
  collection: Collection;
}

const formatPercent = (value: number | null): string =>
  value === null ? '—' : `${Math.round(value * 100)}%`;

const QualityTab: React.FC<Props> = ({ collection }) => {
  const { data, isLoading, isError, refetch } = useCollectionQuality(
    collection.id,
    { enabled: collection.status === 'active' },
  );

  if (collection.status !== 'active') {
    return <EmptyTabState tabName="quality" />;
  }

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="quality-loading">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="h-32 animate-pulse rounded-lg border border-border/70 bg-card"
          />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-[40vh] flex-col items-center justify-center text-center">
        <p className="mb-4 text-muted-foreground">Failed to load quality.</p>
        <Button variant="link" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const overallTone = getIaaTone(data.overallIaa);

  return (
    <div className="space-y-4" data-testid="quality-tab">
      {data.staleDimensions.length > 0 && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900"
          data-testid="quality-stale-banner"
        >
          Some dimensions are no longer in the pinned taxonomy:{' '}
          <span className="font-semibold">{data.staleDimensions.join(', ')}</span>
        </div>
      )}

      {/* 1. Inter-Annotator Agreement */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">
            Inter-Annotator Agreement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div
                className={cn(
                  'text-3xl font-bold tabular-nums',
                  IAA_TONE_TEXT_CLASS[overallTone],
                )}
                data-tone={overallTone}
                data-testid="quality-overall"
              >
                {formatPercent(data.overallIaa)}
              </div>
              <div className="text-[12px] text-muted-foreground">
                Overall agreement (resolved)
              </div>
              <p className="mt-1 max-w-prose text-[11px] leading-snug text-muted-foreground">
                Status-based: records that were adjudicated and then finalised
                count as agreement, so this matches the Overview tab. It can
                read higher than the per-dimension rates below.
              </p>
            </div>

            {data.perDimensionIaa.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-[12px] font-semibold text-foreground">
                  Per-dimension agreement
                </div>
                <p className="max-w-prose text-[11px] leading-snug text-muted-foreground">
                  Raw first-vs-second-pass agreement, measured before
                  adjudication — a different metric, so these need not reconcile
                  with the overall figure above.
                </p>
                <ul
                  className="divide-y divide-border"
                  data-testid="quality-per-dimension"
                >
                  {data.perDimensionIaa.map((row) => {
                  const tone = getIaaTone(row.iaa);
                  return (
                    <li
                      key={row.dimension.key}
                      className="flex items-center justify-between py-2 text-[13px]"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="h-3 w-3 shrink-0 rounded-full"
                          style={{ backgroundColor: getDimensionColor(row.dimension.key) }}
                          aria-hidden="true"
                        />
                        <span className="text-foreground">{row.dimension.name}</span>
                      </div>
                      <span
                        className={cn(
                          'font-semibold tabular-nums',
                          IAA_TONE_TEXT_CLASS[tone],
                        )}
                        data-tone={tone}
                        data-testid={`quality-dim-${row.dimension.key}`}
                      >
                        {formatPercent(row.iaa)}
                      </span>
                    </li>
                  );
                })}
                </ul>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 2. Adjudication Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Adjudication Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3" data-testid="quality-adjudication">
            <AdjTile
              label="Adjudication Rate"
              value={formatPercent(data.adjudication.rate)}
              testid="quality-adj-rate"
            />
            <AdjTile
              label="Disputed"
              value={data.adjudication.disputedCount.toLocaleString()}
              testid="quality-adj-disputed"
            />
            <AdjTile
              label="Agreed"
              value={data.adjudication.agreedCount.toLocaleString()}
              testid="quality-adj-agreed"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AdjTile: React.FC<{ label: string; value: string; testid: string }> = ({
  label,
  value,
  testid,
}) => (
  <div className="rounded-md border border-border bg-card p-3 text-center" data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
    <div className="mt-1 text-xl font-bold tabular-nums text-foreground">{value}</div>
  </div>
);

export default QualityTab;
